"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const lucide_react_1 = require("lucide-react");
const SuggestionPanel_1 = __importDefault(require("./SuggestionPanel"));
const SpellCheckHighlighter_1 = __importDefault(require("./SpellCheckHighlighter"));
const SpellCheckDisplay_1 = __importDefault(require("./SpellCheckDisplay"));
const SpellCheckIndicator_1 = __importDefault(require("./SpellCheckIndicator"));
const spellCheckManager_1 = require("../services/spellCheckManager");
const SubtitleSegmentRow = ({ segment, isActive, onSegmentClick, onTextChange, onApplySuggestion, onRejectSuggestion, onEditingChange, searchTerm }) => {
    const [isEditing, setIsEditing] = (0, react_1.useState)(false);
    const [editText, setEditText] = (0, react_1.useState)(segment.corrected_text);
    const [showSuggestions, setShowSuggestions] = (0, react_1.useState)(false);
    const [spellCheckStatus, setSpellCheckStatus] = (0, react_1.useState)(segment.spellCheck);
    // Register global cache updater for display components
    (0, react_1.useEffect)(() => {
        window.__spellCacheUpdate = (text, result) => {
            try {
                spellCheckManager_1.spellCheckManager.updateCache(segment.segment_id, text, result);
            }
            catch { }
        };
        window.__spellCacheGet = (segmentId, text) => {
            try {
                return spellCheckManager_1.spellCheckManager.getCachedSpellCheck(segmentId, text);
            }
            catch {
                return null;
            }
        };
        return () => { delete window.__spellCacheUpdate; delete window.__spellCacheGet; };
    }, [segment.segment_id]);
    const textAreaRef = (0, react_1.useRef)(null);
    // Simple diff implementation for highlighting changes
    const createSimpleDiff = (original, corrected) => {
        const originalWords = original.split(' ');
        const correctedWords = corrected.split(' ');
        const maxLength = Math.max(originalWords.length, correctedWords.length);
        const originalParts = [];
        const correctedParts = [];
        for (let i = 0; i < maxLength; i++) {
            const origWord = originalWords[i] || '';
            const corrWord = correctedWords[i] || '';
            if (origWord !== corrWord) {
                if (origWord) {
                    originalParts.push({ value: origWord + ' ', removed: true });
                }
                if (corrWord) {
                    correctedParts.push({ value: corrWord + ' ', added: true });
                }
            }
            else if (origWord) {
                originalParts.push({ value: origWord + ' ', unchanged: true });
                correctedParts.push({ value: corrWord + ' ', unchanged: true });
            }
        }
        return { originalParts, correctedParts };
    };
    (0, react_1.useEffect)(() => {
        if (isEditing && textAreaRef.current) {
            textAreaRef.current.focus();
            textAreaRef.current.select();
        }
    }, [isEditing]);
    // Lazy kontrola pravopisu dočasně vypnuta – spouštíme pouze přes tlačítko v Akcích
    (0, react_1.useEffect)(() => {
        return; // disabled
    }, [isActive, segment.segment_id, segment.corrected_text, spellCheckStatus]);
    const formatTime = (timeMs) => {
        const seconds = Math.floor(timeMs / 1000);
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        const ms = timeMs % 1000;
        return `${minutes.toString().padStart(2, '0')}:${remainingSeconds
            .toString()
            .padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
    };
    const handleSave = () => {
        onTextChange(segment.segment_id, editText);
        setIsEditing(false);
        onEditingChange?.(false);
    };
    const handleCancel = () => {
        setEditText(segment.corrected_text);
        setIsEditing(false);
        onEditingChange?.(false);
    };
    const handleStartEditing = () => {
        setIsEditing(true);
        onEditingChange?.(true);
    };
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && e.ctrlKey) {
            handleSave();
        }
        else if (e.key === 'Escape') {
            handleCancel();
        }
    };
    const handleApplySuggestion = (suggestionId) => {
        if (onApplySuggestion) {
            onApplySuggestion(segment.segment_id, suggestionId);
        }
    };
    const handleRejectSuggestion = (suggestionId) => {
        if (onRejectSuggestion) {
            onRejectSuggestion(segment.segment_id, suggestionId);
        }
    };
    const handleApplyAllSuggestions = () => {
        segment.suggestions.forEach(suggestion => {
            if (!suggestion.applied && onApplySuggestion) {
                onApplySuggestion(segment.segment_id, suggestion.suggestion_id);
            }
        });
    };
    const handleRejectAllSuggestions = () => {
        segment.suggestions.forEach(suggestion => {
            if (!suggestion.applied && onRejectSuggestion) {
                onRejectSuggestion(segment.segment_id, suggestion.suggestion_id);
            }
        });
    };
    // Aktualizované stavy podle nové databázové struktury
    const getStatusClassName = () => {
        // Kombinace spell check a AI correction stavů
        const hasSpellErrors = spellCheckStatus?.hasErrors;
        const aiCorrectionType = segment.ai_correction_type || 'unchecked';
        if (hasSpellErrors) {
            return 'border-red-200 bg-red-50';
        }
        switch (aiCorrectionType) {
            case 'needs_review':
                return 'border-orange-200 bg-orange-50';
            case 'ai_corrected':
                return 'border-green-200 bg-green-50';
            case 'unchanged':
                return 'border-gray-200 bg-gray-50';
            default:
                return 'border-gray-200';
        }
    };
    const getStatusColor = () => {
        const hasSpellErrors = spellCheckStatus?.hasErrors;
        const aiCorrectionType = segment.ai_correction_type || 'unchecked';
        if (hasSpellErrors) {
            return 'text-red-600';
        }
        switch (aiCorrectionType) {
            case 'needs_review':
                return 'text-orange-600';
            case 'ai_corrected':
                return 'text-green-600';
            case 'unchanged':
                return 'text-gray-600';
            default:
                return 'text-gray-500';
        }
    };
    // Nová funkce pro zobrazení AI correction indikátoru
    const renderAICorrectionIndicator = () => {
        const aiType = segment.ai_correction_type;
        const confidence = segment.ai_confidence_score;
        if (!aiType || aiType === 'unchecked') {
            return null;
        }
        const getAIIcon = () => {
            switch (aiType) {
                case 'ai_corrected':
                    return (0, jsx_runtime_1.jsx)(lucide_react_1.Bot, { className: "w-3 h-3 text-green-600" });
                case 'needs_review':
                    return (0, jsx_runtime_1.jsx)(lucide_react_1.AlertCircle, { className: "w-3 h-3 text-orange-600" });
                case 'unchanged':
                    return (0, jsx_runtime_1.jsx)(lucide_react_1.CheckCircle, { className: "w-3 h-3 text-gray-600" });
                default:
                    return (0, jsx_runtime_1.jsx)(lucide_react_1.Clock, { className: "w-3 h-3 text-gray-400" });
            }
        };
        const getAILabel = () => {
            switch (aiType) {
                case 'ai_corrected':
                    return 'AI opraveno';
                case 'needs_review':
                    return 'K revizi';
                case 'unchanged':
                    return 'Nezměněno';
                default:
                    return 'Nezpracováno';
            }
        };
        return ((0, jsx_runtime_1.jsxs)("div", { className: "flex items-center space-x-1 px-2 py-1 rounded-full text-xs bg-opacity-10", title: confidence ? `Confidence: ${confidence}%` : undefined, children: [getAIIcon(), (0, jsx_runtime_1.jsx)("span", { className: getStatusColor(), children: getAILabel() }), confidence && ((0, jsx_runtime_1.jsxs)("span", { className: "text-gray-500 ml-1", children: ["(", confidence, "%)"] }))] }));
    };
    const renderTextDiff = () => {
        if (segment.original_text === segment.corrected_text) {
            return (0, jsx_runtime_1.jsx)("span", { className: "text-gray-900", children: segment.corrected_text });
        }
        // Simple diff highlighting
        const { originalParts, correctedParts } = createSimpleDiff(segment.original_text, segment.corrected_text);
        return ((0, jsx_runtime_1.jsxs)("div", { className: "space-y-2", children: [(0, jsx_runtime_1.jsxs)("div", { className: "text-sm", children: [(0, jsx_runtime_1.jsx)("span", { className: "text-gray-500 text-xs uppercase tracking-wide font-medium", children: "P\u016Fvodn\u00ED:" }), (0, jsx_runtime_1.jsx)("div", { className: "mt-1", children: originalParts.map((part, index) => {
                                if (part.removed) {
                                    return ((0, jsx_runtime_1.jsx)("span", { className: "bg-red-100 text-red-800 line-through px-1 rounded", title: "Odstran\u011Bno", children: part.value }, index));
                                }
                                else {
                                    return ((0, jsx_runtime_1.jsx)("span", { className: "text-gray-700", children: part.value }, index));
                                }
                            }) })] }), (0, jsx_runtime_1.jsxs)("div", { className: "text-sm", children: [(0, jsx_runtime_1.jsx)("span", { className: "text-gray-500 text-xs uppercase tracking-wide font-medium", children: "Opraveno:" }), (0, jsx_runtime_1.jsx)("div", { className: "mt-1", children: correctedParts.map((part, index) => {
                                if (part.added) {
                                    return ((0, jsx_runtime_1.jsx)("span", { className: "bg-green-100 text-green-800 px-1 rounded font-medium", title: "P\u0159id\u00E1no/Opraveno", children: part.value }, index));
                                }
                                else {
                                    return ((0, jsx_runtime_1.jsx)("span", { className: "text-gray-900", children: part.value }, index));
                                }
                            }) })] })] }));
    };
    return ((0, jsx_runtime_1.jsx)("div", { className: `p-4 cursor-pointer transition-all ${isActive ? 'bg-blue-50 border-l-4 border-blue-500' : 'hover:bg-gray-50'} ${getStatusClassName()}`, onClick: () => onSegmentClick(segment.segment_id), children: (0, jsx_runtime_1.jsx)("div", { className: "flex items-start justify-between", children: (0, jsx_runtime_1.jsxs)("div", { className: "flex-1", children: [(0, jsx_runtime_1.jsxs)("div", { className: "flex items-center space-x-2 mb-2", children: [(0, jsx_runtime_1.jsx)("span", { className: "text-sm font-mono text-gray-600", children: formatTime(segment.start_time_ms) }), (0, jsx_runtime_1.jsx)("span", { className: "text-gray-400", children: "\u2192" }), (0, jsx_runtime_1.jsx)("span", { className: "text-sm font-mono text-gray-600", children: formatTime(segment.end_time_ms) }), (0, jsx_runtime_1.jsxs)("div", { className: "flex items-center space-x-2 ml-auto", children: [(0, jsx_runtime_1.jsx)(SpellCheckIndicator_1.default, { status: spellCheckStatus, size: "sm", showCount: spellCheckStatus?.hasErrors }), renderAICorrectionIndicator(), segment.manual_edit_count && segment.manual_edit_count > 0 && ((0, jsx_runtime_1.jsxs)("div", { className: "flex items-center space-x-1 px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-600", children: [(0, jsx_runtime_1.jsx)(lucide_react_1.Edit3, { className: "w-3 h-3" }), (0, jsx_runtime_1.jsx)("span", { children: segment.manual_edit_count })] }))] })] }), isEditing ? ((0, jsx_runtime_1.jsxs)("div", { className: "space-y-2", children: [(0, jsx_runtime_1.jsx)("div", { onClick: (e) => e.stopPropagation(), children: (0, jsx_runtime_1.jsx)(SpellCheckHighlighter_1.default, { text: editText, onChange: setEditText, onKeyDown: handleKeyDown, className: "w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none", rows: 2, placeholder: "Upravte text titulku..." }) }), (0, jsx_runtime_1.jsxs)("div", { className: "flex items-center space-x-2", children: [(0, jsx_runtime_1.jsx)("button", { onClick: (e) => {
                                            e.stopPropagation();
                                            handleSave();
                                        }, className: "p-1 text-green-600 hover:bg-green-100 rounded", title: "Ulo\u017Eit zm\u011Bny (Ctrl+Enter)", children: (0, jsx_runtime_1.jsx)(lucide_react_1.Check, { className: "w-4 h-4" }) }), (0, jsx_runtime_1.jsx)("button", { onClick: (e) => {
                                            e.stopPropagation();
                                            handleCancel();
                                        }, className: "p-1 text-red-600 hover:bg-red-100 rounded", title: "Zru\u0161it zm\u011Bny (Esc)", children: (0, jsx_runtime_1.jsx)(lucide_react_1.X, { className: "w-4 h-4" }) })] }), (0, jsx_runtime_1.jsx)("div", { className: "spell-check-tip", children: "Tip: Prav\u00FDm klikem na \u010Derven\u011B podtr\u017Een\u00E1 slova zobraz\u00EDte n\u00E1vrhy oprav" })] })) : ((0, jsx_runtime_1.jsxs)("div", { className: "space-y-2", children: [(0, jsx_runtime_1.jsx)(SpellCheckDisplay_1.default, { text: segment.corrected_text, className: "text-gray-900 cursor-text", onDoubleClick: () => handleStartEditing(), enableSpellCheck: false, showErrors: true, searchTerm: searchTerm, precomputedResult: window.__spellCacheGet?.(segment.segment_id, segment.corrected_text) || null }), (0, jsx_runtime_1.jsx)("button", { onClick: (e) => {
                                    e.stopPropagation();
                                    handleStartEditing();
                                }, className: "p-1 text-gray-400 hover:text-gray-600", title: "Editovat text (F2)", children: (0, jsx_runtime_1.jsx)(lucide_react_1.Edit3, { className: "w-4 h-4" }) })] })), (0, jsx_runtime_1.jsx)(SuggestionPanel_1.default, { suggestions: segment.suggestions, onApplySuggestion: handleApplySuggestion, onRejectSuggestion: handleRejectSuggestion, onApplyAll: handleApplyAllSuggestions, onRejectAll: handleRejectAllSuggestions })] }) }) }));
};
exports.default = SubtitleSegmentRow;
